import { format } from "d3-format";
import React, { Component } from "react";
import PropTypes from "prop-types";

import displayValuesFor from "./displayValuesFor";
import GenericChartComponent from "../GenericChartComponent";

import ToolTipText from "./ToolTipText";
import ToolTipTSpanLabel from "./ToolTipTSpanLabel";
import { functor } from "../utils";

// Canvas-based text measurement utility
let measureCanvas;
function getTextWidth(text, fontFamily = "Arial", fontSize = 12) {
  if (!measureCanvas) {
    measureCanvas = document.createElement("canvas");
  }
  const context = measureCanvas.getContext("2d");
  context.font = `${fontSize}px ${fontFamily}`;
  const metrics = context.measureText(text);
  return metrics.width;
}

const TOOLTIP_BG_HEIGHT = 18;
const TOOLTIP_BG_PADDING = 6;

class PMOTooltip extends Component {
  constructor(props) {
    super(props);
    this.renderSVG = this.renderSVG.bind(this);
  }
  renderSVG(moreProps) {
    const { onClick, fontFamily, fontSize, displayFormat, className, bgFill } =
      this.props;
    const { yAccessor, options, appearance, labelFill } = this.props;
    const { displayValuesFor } = this.props;

    const {
      chartConfig: { width, height },
    } = moreProps;

    const currentItem = displayValuesFor(this.props, moreProps);
    const pmoValue = currentItem && yAccessor(currentItem);

    const pmo =
      (pmoValue && pmoValue.pmo && displayFormat(pmoValue.pmo)) || "n/a";
    const signal =
      (pmoValue && pmoValue.pmoSignal && displayFormat(pmoValue.pmoSignal)) ||
      "n/a";

    const { origin: originProp } = this.props;
    const origin = functor(originProp);
    const [x, y] = origin(width, height);

    // Safety fallbacks for appearance
    const pmoColor = appearance?.strokePMO?.color || labelFill || "#2196F3";
    const signalColor =
      appearance?.strokeSignal?.color || labelFill || "#FF0000";

    // Calculate text width for background
    const fullText = `PMO (${options.smoothingPeriod}, ${options.doubleSmoothingPeriod}, ${options.signalPeriod}): ${pmo} Signal: ${signal}`;
    const textWidth = getTextWidth(
      fullText,
      fontFamily || "Arial",
      fontSize || 12,
    );
    const bgWidth = textWidth + TOOLTIP_BG_PADDING * 2;

    return (
      <g
        className={className}
        transform={`translate(${x}, ${y})`}
        onClick={onClick}
        data-evo-id="tooltip-pmo"
      >
        {/* Background rectangle */}
        <rect
          x={-TOOLTIP_BG_PADDING}
          y={-5}
          width={bgWidth}
          height={TOOLTIP_BG_HEIGHT}
          fill={bgFill || "#1f2937"}
          rx={6}
          ry={6}
        />
        <ToolTipText x={0} y={0} fontFamily={fontFamily} fontSize={fontSize}>
          <ToolTipTSpanLabel fill={labelFill}>PMO (</ToolTipTSpanLabel>
          <tspan fill={pmoColor}>{options.smoothingPeriod}</tspan>
          <ToolTipTSpanLabel fill={labelFill}>, </ToolTipTSpanLabel>
          <tspan fill={pmoColor}>{options.doubleSmoothingPeriod}</tspan>
          <ToolTipTSpanLabel fill={labelFill}>, </ToolTipTSpanLabel>
          <tspan fill={pmoColor}>{options.signalPeriod}</tspan>
          <ToolTipTSpanLabel fill={labelFill}>): </ToolTipTSpanLabel>
          <tspan fill={pmoColor}>{pmo}</tspan>
          <ToolTipTSpanLabel fill={labelFill}> Signal: </ToolTipTSpanLabel>
          <tspan fill={signalColor}>{signal}</tspan>
        </ToolTipText>
      </g>
    );
  }
  render() {
    return (
      <GenericChartComponent
        clip={false}
        svgDraw={this.renderSVG}
        drawOn={["mousemove"]}
      />
    );
  }
}

PMOTooltip.propTypes = {
  origin: PropTypes.oneOfType([PropTypes.array, PropTypes.func]).isRequired,
  className: PropTypes.string,
  fontFamily: PropTypes.string,
  fontSize: PropTypes.number,
  labelFill: PropTypes.string,
  bgFill: PropTypes.string,

  yAccessor: PropTypes.func.isRequired,
  options: PropTypes.shape({
    smoothingPeriod: PropTypes.number.isRequired,
    doubleSmoothingPeriod: PropTypes.number.isRequired,
    signalPeriod: PropTypes.number.isRequired,
  }).isRequired,
  appearance: PropTypes.shape({
    strokePMO: PropTypes.object.isRequired,
    strokeSignal: PropTypes.object.isRequired,
  }).isRequired,
  displayFormat: PropTypes.func.isRequired,
  displayValuesFor: PropTypes.func,
  onClick: PropTypes.func,
};

PMOTooltip.defaultProps = {
  origin: [0, 0],
  displayFormat: format(".3f"),
  displayValuesFor: displayValuesFor,
  className: "react-stockcharts-tooltip",
};

export default PMOTooltip;
